/* Nerve Center v4 — mask-based electrical runtime (Layer D of the canonical
   architecture). The painted topology plate (Layer B) is the visual
   authority: this module never draws replacement curves for the nerves — it
   lights the ACTUAL painted fibers through per-fiber sprite masks + fitted
   guide splines (nerveTopology.ts, generated by
   /app/scripts/build_nerve_assets.py). Cadence is bounded, deterministic and
   weighted by real 24H movement (activity context — never fund flows).
   Zero React state: the rAF loop owns all animation state. */

import type { NucleusPulse, TrafficBurst } from "./nerveTraffic";

export const TAU = Math.PI * 2;
const CYAN = "#00E5FF";
const LILAC = "#E9D5FF";
const WHITE = "#FFFFFF";

export type FxSlot = {
  slug: string; I: number; alpha: number; seed: number;
  P: number[][]; tEnd: number; cx: number; cy: number; r: number;
  exitA: number; fw: number; bbox: number[];
  img: HTMLImageElement | null; tmp: HTMLCanvasElement;
  strands: number[][][];
};
export type FxScene = {
  w: number; h: number; u: number; tier: string; staleF: number; avgI: number;
  cx: number; cy: number; Ri: number; rMid: number; Ro: number;
  paused: boolean; surge: number; q: number; slots: FxSlot[];
};
export type Circuit = { phase: number; tStart: number; nextAt: number };
/* Nucleus Burst — rare all-fiber surge, only when real market movement is
   big (scene.surge > 0). phase 0 idle → 1 charge → 2 cascade blast. */
export type Surge = { phase: number; t0: number; nextAt: number };
export const newSurge = (): Surge => ({ phase: 0, t0: 0, nextAt: performance.now() + 6000 });

export const hashStr = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0) || 1;
};

/* micro-fiber strand geometry — braided 0.5-1px filaments tracing the
   painted bundle (offset + wobble around the fitted guide, tapered so
   they converge at the nucleus root and the satellite arrival). */
export const buildStrands = (P: number[][], fw: number, seed: number, count: number) => {
  const out: number[][][] = [];
  for (let k = 0; k < count; k++) {
    const h1 = hash01(seed + k * 7.3), h2 = hash01(seed + k * 13.7), h3 = hash01(seed + k * 29.1);
    const base = (k / Math.max(1, count - 1) - 0.5) * fw * 0.85;
    const amp = 0.5 + h2 * 2.2;
    const ph = h3 * TAU;
    const pts: number[][] = [];
    for (let j = 0; j <= 40; j++) {
      const t = j / 40;
      const [x, y] = cubic(P, t);
      const [x2, y2] = cubic(P, Math.min(1, t + 0.02));
      const dx = x2 - x, dy = y2 - y, l = Math.hypot(dx, dy) || 1;
      const env = Math.pow(Math.sin(Math.PI * t), 0.5);
      const off = base * env + Math.sin(t * (6 + h1 * 8) + ph) * amp * (0.4 + 0.6 * env);
      pts.push([x + (-dy / l) * off, y + (dx / l) * off]);
    }
    out.push(pts);
  }
  return out;
};

export const cubic = (P: number[][], t: number): number[] => {
  const u = 1 - t;
  return [
    u * u * u * P[0][0] + 3 * u * u * t * P[1][0] + 3 * u * t * t * P[2][0] + t * t * t * P[3][0],
    u * u * u * P[0][1] + 3 * u * u * t * P[1][1] + 3 * u * t * t * P[2][1] + t * t * t * P[3][1],
  ];
};
const hash01 = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
const easeIO = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const dot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, col: string, a: number) => {
  if (a <= 0.01) return;
  ctx.globalAlpha = Math.min(1, a);
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.4, r), 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
};
const bloom = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, col: string, a: number) => {
  if (a <= 0.01 || r <= 1) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, col);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = Math.min(1, a);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
};
const ring = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number, col: string, w: number) => {
  if (a <= 0.01 || r <= 1) return;
  ctx.globalAlpha = Math.min(1, a);
  ctx.strokeStyle = col;
  ctx.lineWidth = w;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.stroke();
  ctx.globalAlpha = 1;
};
const arc = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, a0: number, a1: number, w: number, style: string) => {
  if (r <= 1) return;
  ctx.strokeStyle = style;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(cx, cy, r, a0, a1);
  ctx.stroke();
};
const flare = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number, col: string) => {
  if (a <= 0.01) return;
  ctx.globalAlpha = Math.min(1, a);
  ctx.strokeStyle = col;
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(x - r, y); ctx.lineTo(x + r, y);
  ctx.moveTo(x, y - r); ctx.lineTo(x, y + r);
  ctx.stroke();
  ctx.globalAlpha = 1;
  dot(ctx, x, y, r * 0.24, WHITE, a);
};
const guidePath = (ctx: CanvasRenderingContext2D, s: FxSlot, t0: number, t1: number, n = 20) => {
  ctx.beginPath();
  for (let k = 0; k <= n; k++) {
    const [x, y] = cubic(s.P, t0 + ((t1 - t0) * k) / n);
    k ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
  }
};

export const syncCircuits = (map: Map<string, Circuit>, scene: FxScene, now: number) => {
  scene.slots.forEach((s, i) => {
    if (!map.has(s.slug)) map.set(s.slug, { phase: 0, tStart: 0, nextAt: now + 700 + i * 1250 * (1.4 - s.I) });
  });
  Array.from(map.keys()).forEach((k) => { if (!scene.slots.some((s) => s.slug === k)) map.delete(k); });
};
export const fireSoon = (map: Map<string, Circuit>, slug: string, now: number, delay = 170) => {
  const c = map.get(slug);
  if (c && c.phase === 0 && c.nextAt > now + delay + 60) c.nextAt = now + delay;
};
export const fireState = (c: Circuit | undefined, now: number) =>
  !c ? "" : c.phase === 2 ? "hit" : c.phase === 3 && now - c.tStart < 340 ? "send" : "";

/* Progressive illumination of the PAINTED fiber (spec §44): a reveal-stroke
   along the fitted guide spline clips the per-fiber sprite, so the artwork
   itself lights from the core outward. */
function fiberLight(ctx: CanvasRenderingContext2D, s: FxSlot, t0: number, t1: number, alpha: number, withSocket = false) {
  if (alpha <= 0.015 || !s.img || !s.img.complete || !s.img.naturalWidth) return;
  const [bx, by, bw, bh] = s.bbox;
  const W = Math.ceil(bw), H = Math.ceil(bh);
  if (W < 2 || H < 2) return;
  const tc = s.tmp;
  if (tc.width !== W || tc.height !== H) { tc.width = W; tc.height = H; }
  const t = tc.getContext("2d");
  if (!t) return;
  t.setTransform(1, 0, 0, 1, 0, 0);
  t.clearRect(0, 0, W, H);
  t.setTransform(1, 0, 0, 1, -bx, -by);
  t.globalCompositeOperation = "source-over";
  t.strokeStyle = WHITE;
  t.fillStyle = WHITE;
  t.lineWidth = Math.max(10, s.fw * 1.7);
  t.lineCap = "round";
  t.lineJoin = "round";
  guidePath(t as any, s, Math.max(0, t0), Math.min(1, t1));
  t.stroke();
  if (withSocket || t1 >= s.tEnd - 0.02) {
    t.beginPath();
    t.arc(s.cx, s.cy, s.r * 1.4, 0, TAU);
    t.fill();
  }
  t.setTransform(1, 0, 0, 1, 0, 0);
  t.globalCompositeOperation = "source-in";
  t.drawImage(s.img, 0, 0, W, H);
  ctx.globalAlpha = Math.min(1, alpha);
  ctx.drawImage(tc, bx, by);
  ctx.globalAlpha = 1;
}

export function drawMotionFrame(
  ctx: CanvasRenderingContext2D, scene: FxScene, circuits: Map<string, Circuit>, now: number,
  o: { dpr: number; activeSlug: string | null; debug?: boolean; surge?: Surge;
    traffic?: { bursts: TrafficBurst[]; pulse: NucleusPulse; suppress: Set<string> } },
) {
  const { w, h, u, cx, cy, Ri, rMid, Ro, tier } = scene;
  const q = scene.q ?? 1; // adaptive quality: 1 full · <1 lite (low-power devices)
  ctx.setTransform(o.dpr, 0, 0, o.dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  /* interaction priority — recede non-active painted fibers (normal blend).
     Live traffic gets the same spotlight: while a major current conducts,
     competing fibers dim so origin → travel → arrival reads instantly. */
  const liveBursts = (o.traffic && !scene.paused)
    ? o.traffic.bursts.filter((b) => { const k = (now - b.t0) / b.dur; return b.tier > 0 && k >= 0 && k <= 1; })
    : [];
  const burstSlugs = new Set(liveBursts.map((b) => b.slug));
  let burstEnv = 0;
  liveBursts.forEach((b) => {
    const k = (now - b.t0) / b.dur;
    burstEnv = Math.max(burstEnv, Math.min(1, Math.min(k * 4, (1 - k) * 4)) * (0.5 + 0.25 * b.tier));
  });
  if (o.activeSlug || burstSlugs.size) {
    ctx.globalCompositeOperation = "source-over";
    ctx.lineCap = "round";
    scene.slots.forEach((s) => {
      if (s.slug === o.activeSlug || s.alpha < 0.4) return;
      const spotlit = burstSlugs.has(s.slug) && !o.activeSlug;
      if (spotlit) return;
      const a = o.activeSlug ? 0.34 : Math.min(0.3, 0.26 * burstEnv);
      if (a < 0.02) return;
      ctx.strokeStyle = `rgba(4,4,12,${a.toFixed(3)})`;
      ctx.lineWidth = Math.max(14, s.fw * 2.1);
      guidePath(ctx, s, 0.02, s.tEnd);
      ctx.stroke();
    });
  }

  ctx.globalCompositeOperation = "lighter";

  /* LAYER A — ambient life: nucleus breathing, counter-rotating membrane
     shimmer, membrane spark flickers */
  const br = Math.max(0, 0.05 + 0.035 * Math.sin(now * 0.00052) + 0.02 * Math.sin(now * 0.00113 + 2));
  bloom(ctx, cx, cy, Ri * 1.5, "rgba(124,58,237,.6)", br);
  bloom(ctx, cx, cy, Ro * 1.2, "rgba(0,229,255,.25)", Math.max(0, 0.03 + 0.02 * Math.sin(now * 0.0004 + 1)));
  if (tier !== "mobile" && q >= 1) {
    const a1 = (now * 0.00006) % TAU, a2 = -(now * 0.000042) % TAU;
    arc(ctx, cx, cy, rMid + 3 * u, a1, a1 + 0.9, 3.5 * u, "rgba(167,139,250,.10)");
    arc(ctx, cx, cy, rMid - 4 * u, a2, a2 + 0.7, 3 * u, "rgba(0,229,255,.08)");
  }
  for (let k = 0; k < (q < 1 ? 2 : 4); k++) {
    const bkt = Math.floor(now / 760) + k * 97;
    if (hash01(bkt + 2) > 0.72) continue;
    const tw = Math.sin(Math.PI * ((now % 760) / 760));
    const a = hash01(bkt) * TAU, r = rMid + (hash01(bkt + 1) - 0.5) * (Ro - Ri);
    flare(ctx, cx + Math.cos(a) * r, cy + Math.sin(a) * r, (2.5 + hash01(bkt + 3) * 3) * u, 0.35 * tw, hash01(bkt + 4) > 0.5 ? CYAN : LILAC);
  }

  /* NUCLEUS VOICE — living frequency fluctuations of the membrane: slow
     breathing amplitude, layered harmonics, refraction drift. On a BUY
     impact the waveform distorts much faster (plasma membrane under
     pressure — never an equalizer, never a CSS pulse). */
  if (!scene.paused) {
    const pl = o.traffic?.pulse;
    const vPulse = pl && pl.power > 0 && now - pl.t0 < 750 ? (1 - (now - pl.t0) / 750) * Math.min(1, pl.power) : 0;
    /* organic amplitude: layered incommensurate slow sines + per-harmonic
       weight drift — irregular plasma fluctuation, never a periodic dial */
    const vAmp = (1 + 0.45 * Math.sin(now * 0.00019) + 0.25 * Math.sin(now * 0.00047 + 1.3) + 0.18 * Math.sin(now * 0.00083 + 4.1))
      * (1 + 1.6 * vPulse) * 1.05 * u;
    const w1 = 0.75 + 0.25 * Math.sin(now * 0.00031);
    const w2 = 0.7 + 0.3 * Math.sin(now * 0.00023 + 2);
    const w3 = 0.5 + 0.5 * Math.sin(now * 0.00041 + 5);
    const vDrift = now * (0.00035 + 0.0012 * vPulse);
    const N = q < 1 ? 34 : 52;
    const voice = (r0: number, f1: number, f2: number, col: string, wd: number, a: number) => {
      if (a <= 0.01) return;
      ctx.globalAlpha = Math.min(1, a);
      ctx.strokeStyle = col;
      ctx.lineWidth = wd;
      ctx.beginPath();
      for (let i = 0; i <= N; i++) {
        const th = (i / N) * TAU;
        const rr = r0 + Math.sin(th * f1 + vDrift) * vAmp * w1
          + Math.sin(th * f2 - vDrift * 1.6) * vAmp * 0.55 * w2
          + Math.sin(th * 11 + vDrift * 0.7) * vAmp * 0.3 * w3;
        const x = cx + Math.cos(th) * rr, y = cy + Math.sin(th) * rr;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.globalAlpha = 1;
    };
    voice(rMid + 1.5 * u, 3, 7, "rgba(167,139,250,.55)", 1.05 * u,
      0.14 + 0.07 * Math.sin(now * 0.00052) + 0.22 * vPulse);
    if (q >= 1) voice(rMid - 5 * u, 5, 9, "rgba(0,229,255,.5)", 0.8 * u,
      0.09 + 0.05 * Math.sin(now * 0.00034 + 2) + 0.18 * vPulse);
  }

  /* NUCLEUS BURST MOMENTS — rare big-market surge (§52 truth: gated by real
     measured movement, never decorative). Charge-up, then every painted
     nerve fires in a cascade, then a full-ring shockwave finale. */
  const sg = o.surge;
  let surgeBlast = false;
  if (sg && scene.surge > 0 && !scene.paused) {
    const CHARGE = 950, BLAST = 3800;
    if (sg.phase === 0 && now >= sg.nextAt && !o.activeSlug) { sg.phase = 1; sg.t0 = now; }
    else if (sg.phase === 1 && now - sg.t0 >= CHARGE) {
      sg.phase = 2;
      sg.t0 = now;
      scene.slots.forEach((s, i) => {
        const c = circuits.get(s.slug);
        if (c && c.phase === 0) { c.phase = 1; c.tStart = now + i * 90; }
      });
    } else if (sg.phase === 2 && now - sg.t0 >= BLAST) {
      sg.phase = 0;
      sg.nextAt = now + 22000 + hash01(Math.floor(now)) * 9000;
    }
    if (sg.phase === 1) {
      const k = (now - sg.t0) / CHARGE;
      bloom(ctx, cx, cy, Ri * (0.7 + 0.9 * k), "rgba(190,240,255,.8)", 0.25 * k * scene.surge);
      bloom(ctx, cx, cy, Ro * (0.9 + 0.3 * k), "rgba(124,58,237,.7)", 0.3 * k * scene.surge);
      ring(ctx, cx, cy, rMid * (1 - 0.06 * k), 0.35 * k * scene.surge, "#C9B8FF", 2 * u);
      for (let q = 0; q < 5; q++) {
        const a = hash01(Math.floor(now / 90) + q * 13) * TAU;
        flare(ctx, cx + Math.cos(a) * rMid, cy + Math.sin(a) * rMid, 3.5 * u, 0.5 * k, "#7DF9FF");
      }
    } else if (sg.phase === 2) {
      surgeBlast = true;
      const k = (now - sg.t0) / BLAST;
      bloom(ctx, cx, cy, Ri * 1.2, "rgba(0,229,255,.5)", 0.2 * (1 - k) * scene.surge);
      if (k > 0.84) {
        const fk = (k - 0.84) / 0.16;
        ring(ctx, cx, cy, rMid + (Ro - rMid + 30 * u) * fk * 2.1, (1 - fk) * 0.5 * scene.surge, "#7DF9FF", 2.2 * u);
        ring(ctx, cx, cy, rMid + (Ro - rMid + 30 * u) * fk * 1.4, (1 - fk) * 0.35 * scene.surge, "#A78BFA", 1.6 * u);
        bloom(ctx, cx, cy, Ri * (1 + fk), "rgba(190,240,255,.7)", (1 - fk) * 0.3 * scene.surge);
      }
    }
  }

  /* LAYERS B/C/D — micro activity + closed loops + data-driven bursts.
     Bounded scheduler: max 3 concurrent major loops (§55-56) — lifted
     during a Nucleus Burst so the whole organism fires. */
  const burst = scene.avgI > 0.55;
  const boost = surgeBlast ? 1.3 : 1;
  let majors = scene.slots.reduce((n, s) => n + ((circuits.get(s.slug)?.phase ?? 0) > 0 ? 1 : 0), 0);

  scene.slots.forEach((s, i) => {
    const c = circuits.get(s.slug);
    if (!c || s.alpha < 0.15) return;
    const I = s.I;
    const dim = (o.activeSlug != null && o.activeSlug !== s.slug ? 0.55 : 1) * s.alpha;

    if (!scene.paused) {
      const mBkt = Math.floor(now / 640) * 31 + i * 7;
      if (hash01(mBkt) < (0.13 + 0.2 * I) * q) {
        const tw = Math.sin(Math.PI * ((now % 640) / 640));
        const t0f = 0.06 + hash01(mBkt + 5) * 0.6;
        fiberLight(ctx, s, t0f, t0f + 0.16, 0.28 * tw * dim);
      }
    }

    const outDur = (2350 - 1200 * I) * (burst ? 0.85 : 1);
    const retDur = outDur * 1.12, hitDur = 300, impDur = 540;
    const cool = ((4400 - 2300 * I) * (burst ? 0.75 : 1) + (i % 3) * 760) * scene.staleF;
    if (c.phase === 0) {
      if (!scene.paused && now >= c.nextAt) {
        /* classified traffic replaces the neutral (L3) loop for this entity */
        if (o.traffic?.suppress?.has(s.slug)) c.nextAt = now + 2400;
        else if (majors < (surgeBlast ? 99 : q < 1 ? 2 : 3)) { c.phase = 1; c.tStart = now; majors++; }
        else c.nextAt = now + 420 + i * 140;
      }
    } else if (c.phase === 1 && now - c.tStart >= outDur) { c.phase = 2; c.tStart = now; }
    else if (c.phase === 2 && now - c.tStart >= hitDur) { c.phase = 3; c.tStart = now; }
    else if (c.phase === 3 && now - c.tStart >= retDur) { c.phase = 4; c.tStart = now; }
    else if (c.phase === 4 && now - c.tStart >= impDur) { c.phase = 0; c.nextAt = now + cool; }

    if (c.phase === 1 && now >= c.tStart) {
      /* outbound — the painted nerve wakes progressively behind the current */
      const p = Math.min(1, Math.max(0, (now - c.tStart) / outDur));
      const tt = easeIO(p) * s.tEnd;
      fiberLight(ctx, s, 0, tt, (0.42 + 0.3 * I) * dim * boost);
      const [hx, hy] = cubic(s.P, tt);
      bloom(ctx, hx, hy, (9 + 6 * I) * u * boost, "rgba(190,240,255,.85)", 0.5 * dim * boost);
      dot(ctx, hx, hy, (1.6 + 1.1 * I) * u, WHITE, 0.95 * dim);
      if ((burst || surgeBlast) && I > 0.6) {
        const [sx2, sy2] = cubic(s.P, Math.max(0, tt - 0.14));
        dot(ctx, sx2, sy2, 1.1 * u, "#9FF2FF", 0.6 * dim);
      }
      if (p < 0.28) bloom(ctx, s.P[0][0], s.P[0][1], 15 * u, "rgba(190,240,255,.7)", (0.28 - p) * 1.9 * dim);
    } else if (c.phase === 2) {
      /* arrival — satellite receives charge */
      const k = (now - c.tStart) / hitDur;
      fiberLight(ctx, s, 0, s.tEnd, (1 - k) * 0.42 * dim, true);
      ring(ctx, s.cx, s.cy, s.r * (1.05 + 0.55 * k), (1 - k) * 0.85 * dim, "#7DF9FF", 1.6 * u);
      ring(ctx, s.cx, s.cy, s.r * (1.3 + 0.7 * k), (1 - k) * 0.35 * dim, CYAN, u);
      bloom(ctx, s.cx, s.cy, s.r * (1.4 + 0.4 * k), "rgba(0,229,255,.7)", (1 - k) * 0.55 * dim);
    } else if (c.phase === 3) {
      /* return — same painted nerve relights in reverse (§48) */
      const p = Math.min(1, (now - c.tStart) / retDur);
      const tt = (1 - easeIO(p)) * s.tEnd;
      fiberLight(ctx, s, Math.max(0, tt - 0.05), s.tEnd, (0.34 + 0.2 * I) * (1 - p * 0.55) * dim * boost, true);
      const [hx, hy] = cubic(s.P, tt);
      bloom(ctx, hx, hy, (8 + 5 * I) * u, "rgba(167,139,250,.85)", 0.5 * dim);
      dot(ctx, hx, hy, (1.5 + I) * u, WHITE, 0.9 * dim);
    } else if (c.phase === 4) {
      /* nucleus receive (§49) — impact spark, membrane waveform flutter,
         layered plasma sound-wave ripples, chamber pulse, rebound */
      const k = (now - c.tStart) / impDur;
      const [ex, ey] = s.P[0];
      bloom(ctx, ex, ey, (16 + 26 * k) * u, "rgba(190,240,255,.85)", (1 - k) * 0.8);
      dot(ctx, ex, ey, 2.2 * u, WHITE, 1 - k);
      const span = 0.3 + 0.85 * k;
      arc(ctx, cx, cy, rMid + 2 * u, s.exitA - span, s.exitA + span, 2.6 * u, `rgba(167,139,250,${(0.6 * (1 - k)).toFixed(3)})`);
      arc(ctx, cx, cy, rMid - 6 * u, s.exitA - span * 0.7, s.exitA + span * 0.7, 1.8 * u, `rgba(0,229,255,${(0.5 * (1 - k)).toFixed(3)})`);
      for (let wv = 0; wv < 3; wv++) {
        const wk = k * 1.4 - wv * 0.14;
        if (wk <= 0 || wk >= 1) continue;
        const rr = rMid + Math.sin(k * 10 + wv * 2.1) * (3 + wv * 2) * u;
        arc(ctx, cx, cy, rr, s.exitA - 0.5 - wv * 0.2, s.exitA + 0.5 + wv * 0.2, 0.9 * u, `rgba(233,213,255,${(0.3 * (1 - wk)).toFixed(3)})`);
      }
      for (let wv = 0; wv < 3; wv++) {
        const wk = (k - wv * 0.16) / (1 - wv * 0.16);
        if (wk <= 0 || wk > 1) continue;
        ring(ctx, cx, cy, Ri + (Ro - Ri + 14 * u) * wk, (1 - wk) * (0.3 - wv * 0.07), wv % 2 ? CYAN : "#A78BFA", (1.4 - wv * 0.3) * u);
      }
      bloom(ctx, cx, cy, Ri * 0.95, "rgba(124,58,237,.6)", (1 - k) * 0.26);
      bloom(ctx, cx, cy, Ri * 0.5, "rgba(0,229,255,.45)", (1 - k) * 0.18);
      fiberLight(ctx, s, 0, 0.16, (1 - k) * 0.4 * dim);
    }
  });

  /* ── LIVE ELECTRICAL TRAFFIC — classified BUY (inward, cyan/mint/white)
     and SELL (outward, violet/purple/white) micro-fiber conduction. Only
     rendered when the engine produced qualifying bursts (truth-gated). */
  const tr = o.traffic;
  if (tr && tr.bursts.length && !scene.paused) {
    tr.bursts.forEach((b) => {
      const s = scene.slots.find((x) => x.slug === b.slug);
      if (!s || s.alpha < 0.3) return;
      const k = (now - b.t0) / b.dur;
      if (k < 0 || k > 1) return;
      const e = easeIO(k);
      const micro = b.tier === 0; // §MICRO: fine · fast · delicate · perceptible
      const am = micro ? 0.55 : 1; // amplitude scale — value drives amplitude
      const dens = scene.tier === "mobile" ? b.density * 0.6 : b.density;
      const tHead = b.dir === 1 ? 0.02 + (s.tEnd - 0.02) * (1 - e) : 0.02 + (s.tEnd - 0.02) * e;
      const trail = micro ? 0.13 + 0.07 * dens : 0.22 + 0.12 * dens;
      /* energized section = behind the current front */
      const t0 = b.dir === 1 ? tHead : Math.max(0.02, tHead - trail);
      const t1 = b.dir === 1 ? Math.min(s.tEnd, tHead + trail) : tHead;
      /* the painted branch itself conducts — soft wake-up glow behind the
         strands (structure preserved, never one blurred tube) */
      fiberLight(ctx, s, Math.max(0, t0 - 0.05), Math.min(s.tEnd, t1 + 0.05), (micro ? 0.18 + 0.1 * dens : 0.3 + 0.14 * dens) * s.alpha);
      drawStrandCurrent(ctx, s, b, dens, t0, t1, now, q);
      /* §hot-front: small white-hot ionization glint at the leading edge +
         tight directional halo — the eye must TRACK the moving front */
      const [hx, hy] = cubic(s.P, tHead);
      const headCol = b.dir === 1 ? "rgba(110,231,183,.9)" : "rgba(196,181,253,.92)";
      bloom(ctx, hx, hy, (micro ? 6.5 + 2 * b.conc : 11 + 6 * b.conc + 3 * b.tier) * u, headCol, (micro ? 0.55 : 0.68) * s.alpha);
      bloom(ctx, hx, hy, (micro ? 2.8 : 4.5) * u, "rgba(255,255,255,1)", (micro ? 0.9 : 0.95) * s.alpha);
      dot(ctx, hx, hy, (micro ? 1.25 : 1.7) * u, WHITE, s.alpha);
      /* §wake: short graded conduction wake behind the front —
         100% → ~75% → ~45% → ambient over 10-20% of the path */
      const wakeCols = b.dir === 1
        ? ["#FFFFFF", "#7DF9FF", "#6EE7B7", "#22D3EE", "#6EE7B7", "#22D3EE"]
        : ["#FFFFFF", "#E9D5FF", "#C4B5FD", "#A78BFA", "#C4B5FD", "#A78BFA"];
      const wakeSpan = ((micro ? 0.1 : 0.15) + 0.05 * dens) * s.tEnd;
      const wakeN = micro ? 4 : 6;
      for (let wq = 1; wq <= wakeN; wq++) {
        const f = wq / wakeN;
        const tw = b.dir === 1 ? Math.min(s.tEnd, tHead + f * wakeSpan) : Math.max(0.02, tHead - f * wakeSpan);
        const [wx, wy] = cubic(s.P, tw);
        const aa = Math.pow(1 - f, 1.15) * (micro ? 0.8 : 0.9) * s.alpha;
        dot(ctx, wx, wy, (1.35 - f * 0.75) * u * (micro ? 0.95 : 1.25), wakeCols[wq - 1], aa);
        if (wq <= 2) bloom(ctx, wx, wy, (4.5 - f * 2) * u, headCol, aa * 0.45);
      }
      if (b.dir === -1 && k < 0.15) bloom(ctx, s.P[0][0], s.P[0][1], 16 * u * am, "rgba(196,181,253,.9)", (0.15 - k) * 5 * s.alpha * am);
      if (b.dir === 1 && k < 0.12) {
        ring(ctx, s.cx, s.cy, s.r * 1.08, (0.12 - k) * 6 * s.alpha * am, "#6EE7B7", 1.4 * u);
        if (!micro) bloom(ctx, s.cx, s.cy, s.r * 1.2, "rgba(110,231,183,.7)", (0.12 - k) * 3.4 * s.alpha);
      }
      /* BUY approach — nucleus entry point warms as the current closes in */
      if (b.dir === 1 && k > 0.8) {
        bloom(ctx, s.P[0][0], s.P[0][1], (10 + 14 * (k - 0.8) * 5) * u * am, "rgba(110,231,183,.8)", (k - 0.8) * 3 * s.alpha * am);
      }
      /* SELL arrival — satellite gets its strong reaction ON ARRIVAL:
         violet ring pulse + local bloom + arrival sparklets (micro: glint) */
      if (b.dir === -1 && k > 0.82) {
        const ak = (k - 0.82) / 0.18;
        ring(ctx, s.cx, s.cy, s.r * (1.06 + 0.5 * ak), (1 - ak) * 0.85 * s.alpha * am, "#A78BFA", 1.6 * u);
        if (!micro) {
          ring(ctx, s.cx, s.cy, s.r * (1.26 + 0.8 * ak), (1 - ak) * 0.38 * s.alpha, "#7C3AED", u);
          bloom(ctx, s.cx, s.cy, s.r * (1.35 + 0.5 * ak), "rgba(167,139,250,.75)", (1 - ak) * 0.55 * s.alpha);
        }
        for (let sq = 0; sq < (micro ? 1 : 3); sq++) {
          const a = hash01(b.seed + sq * 47) * TAU;
          flare(ctx, s.cx + Math.cos(a) * s.r * 1.15, s.cy + Math.sin(a) * s.r * 1.15,
            2.6 * u * am, (1 - ak) * 0.55 * s.alpha, sq % 2 ? "#E9D5FF" : WHITE);
        }
      }
    });
  }
  /* nucleus receive reaction — merged inbound BUY pressure (bounded).
     Founder-locked impact sequence: entry sparkle → membrane COMPRESSION
     (~180ms) → internal energy flash → REBOUND overshoot (~270ms) →
     waveform shell ripple (→750ms) → sparkle eruption → settle.
     Shell/membrane reacts — core text never moves. */
  if (tr && tr.pulse.power > 0) {
    const pk = (now - tr.pulse.t0) / 750;
    if (pk >= 0 && pk <= 1) {
      const P = tr.pulse.power;
      if (tr.pulse.exit) {
        const [ex, ey] = tr.pulse.exit;
        bloom(ctx, ex * w, ey * h, (14 + 22 * pk) * u, "rgba(110,231,183,.9)", (1 - pk) * 0.85 * P);
        dot(ctx, ex * w, ey * h, 2.2 * u, WHITE, (1 - pk) * P);
        const ea = Math.atan2(ey * h - cy, ex * w - cx);
        const span = 0.28 + 0.7 * pk;
        arc(ctx, cx, cy, rMid + 2 * u, ea - span, ea + span, 2.6 * u, `rgba(110,231,183,${(0.6 * (1 - pk) * P).toFixed(3)})`);
      }
      /* membrane displacement: compress -8.3% → rebound overshoot → settle
         (§14: impact unmistakable ~300-500ms, never a CSS scale) */
      const mem = pk < 0.24 ? -0.083 * easeIO(pk / 0.24)
        : pk < 0.6 ? -0.083 + 0.106 * easeIO((pk - 0.24) / 0.36)
        : 0.023 * (1 - easeIO((pk - 0.6) / 0.4));
      ring(ctx, cx, cy, rMid * (1 + mem), (1 - pk * 0.8) * 0.7 * P, "#7DF9FF", 2 * u);
      ring(ctx, cx, cy, (rMid - 5 * u) * (1 + mem * 1.25), (1 - pk) * 0.38 * P, "#6EE7B7", 1.1 * u);
      /* internal energy flashes brighter during compression + white-hot accent */
      bloom(ctx, cx, cy, Ri * (0.85 + 0.35 * pk), "rgba(0,229,255,.55)", (pk < 0.3 ? 0.56 : (1 - pk) * 0.46) * P);
      bloom(ctx, cx, cy, Ri * 0.5, "rgba(110,231,183,.5)", (1 - pk) * 0.3 * P);
      bloom(ctx, cx, cy, Ri * 0.32, "rgba(255,255,255,.9)", Math.max(0, (pk < 0.22 ? 0.4 : 0.4 - (pk - 0.22) * 1.1)) * P);
      /* waveform / sound-wave shell ripples */
      for (let wv = 0; wv < 2 + Math.min(2, tr.pulse.n); wv++) {
        const wk = (pk - wv * 0.14) / (1 - wv * 0.14);
        if (wk <= 0 || wk > 1) continue;
        const rr = Ri + (Ro - Ri + 12 * u) * wk + Math.sin(wk * 9 + wv * 1.7) * 2.4 * u;
        ring(ctx, cx, cy, rr, (1 - wk) * (0.34 - wv * 0.06) * P, wv % 2 ? "#6EE7B7" : CYAN, (1.4 - wv * 0.25) * u);
      }
      /* sparkle eruption on the membrane */
      for (let q = 0; q < 6; q++) {
        const a = hash01(Math.floor(tr.pulse.t0) + q * 31) * TAU;
        const er = rMid + pk * (7 + hash01(q * 17) * 9) * u;
        flare(ctx, cx + Math.cos(a) * er, cy + Math.sin(a) * er, (2.4 + hash01(q * 7) * 1.6) * u,
          (1 - pk) * 0.55 * P, q % 3 === 0 ? WHITE : q % 2 ? "#6EE7B7" : "#7DF9FF");
      }
    }
  }

  if (o.debug) drawDebug(ctx, scene);
}

const BUY_COLS = ["#00E5FF", "#6EE7B7", "#7DF9FF", "#FFFFFF", "#22D3EE", "#FFFFFF"];
const SELL_COLS = ["#A78BFA", "#7C3AED", "#E9D5FF", "#FFFFFF", "#C4B5FD", "#F0ABFC"];

/* Braided strand conduction, clipped to the painted artwork (destination-in
   fiber mask) — nerve conduction, not particle animation. */
function drawStrandCurrent(ctx: CanvasRenderingContext2D, s: FxSlot, b: TrafficBurst, dens: number, t0: number, t1: number, now: number, q = 1) {
  if (!s.img || !s.img.complete || !s.img.naturalWidth || !s.strands.length) return;
  const [bx, by, bw, bh] = s.bbox;
  const W = Math.ceil(bw), H = Math.ceil(bh);
  if (W < 2 || H < 2) return;
  const tc = s.tmp;
  if (tc.width !== W || tc.height !== H) { tc.width = W; tc.height = H; }
  const t = tc.getContext("2d");
  if (!t) return;
  t.setTransform(1, 0, 0, 1, 0, 0);
  t.globalCompositeOperation = "source-over";
  t.clearRect(0, 0, W, H);
  t.setTransform(1, 0, 0, 1, -bx, -by);
  t.lineCap = "round";
  const cols = b.dir === 1 ? BUY_COLS : SELL_COLS;
  const micro = b.tier === 0;
  /* §micro-fiber hero: micro trade = 1-3 fine threads; larger value =
     more adjacent strands + width + brightness (value → amplitude) */
  const nStr = micro ? 1 + Math.round(2 * dens)
    : Math.max(3, Math.round(s.strands.length * (0.35 + 0.65 * dens) * (0.65 + 0.15 * b.tier) * q));
  const last = s.strands[0].length - 1;
  for (let i = 0; i < nStr; i++) {
    const st = s.strands[(i * 7 + b.seed) % s.strands.length];
    const h = hash01(b.seed + i * 13);
    const dSh = (h - 0.5) * 0.05; // per-strand start delay
    const a0 = Math.max(0, Math.min(1, t0 + dSh)), a1 = Math.max(0, Math.min(1, t1 + dSh));
    if (a1 - a0 < 0.01) continue;
    const i0 = Math.floor(a0 * last), i1 = Math.ceil(a1 * last);
    t.strokeStyle = cols[(i + (b.seed % 3)) % cols.length];
    t.globalAlpha = (micro ? 0.62 + 0.33 * hash01(b.seed + i * 29) : (0.42 + 0.5 * hash01(b.seed + i * 29)) * Math.min(1, 0.55 + b.conc));
    t.lineWidth = micro ? 0.5 + 0.35 * hash01(b.seed + i * 31) : 0.6 + 0.65 * b.conc * hash01(b.seed + i * 31);
    t.beginPath();
    for (let j = i0; j <= i1; j++) (j === i0 ? t.moveTo(st[j][0], st[j][1]) : t.lineTo(st[j][0], st[j][1]));
    t.stroke();
    /* §micro-arc: sparse tiny current jump between adjacent threads —
       electricity moving through a bundled nerve (majors + rare on micros) */
    if (q >= 1 && s.strands.length > 1 && hash01(Math.floor(now / 240) + i * 17 + b.seed) < (micro ? 0.09 : 0.22)) {
      const st2 = s.strands[((i + 1) * 7 + b.seed) % s.strands.length];
      const jj = Math.floor((a0 + (a1 - a0) * hash01(b.seed + i * 41)) * last);
      t.globalAlpha = 0.5;
      t.lineWidth = 0.5;
      t.beginPath();
      t.moveTo(st[jj][0], st[jj][1]);
      t.lineTo(st2[jj][0], st2[jj][1]);
      t.stroke();
    }
  }
  /* §sparklets ride WITH the electrical front — clustered at the head,
     progressing directionally with it, twinkling without re-scattering */
  const nSp = micro ? 2 + Math.round(dens) : 3 + Math.round(4 * dens * q);
  t.fillStyle = "#FFFFFF";
  for (let sp = 0; sp < nSp; sp++) {
    const off = Math.pow(hash01(b.seed + sp * 53), 1.6) * (t1 - t0);
    const tq = b.dir === 1 ? Math.min(1, t0 + off) : Math.max(0, t1 - off);
    const st = s.strands[(sp * 11 + b.seed) % s.strands.length];
    const p = st[Math.max(0, Math.min(last, Math.round(tq * last)))];
    t.globalAlpha = 0.5 + 0.45 * hash01(b.seed + sp * 59 + Math.floor(now / 130));
    t.beginPath();
    t.arc(p[0], p[1], 0.6 + hash01(b.seed + sp * 61) * 0.9, 0, TAU);
    t.fill();
  }
  t.globalAlpha = 1;
  t.setTransform(1, 0, 0, 1, 0, 0);
  t.globalCompositeOperation = "destination-in";
  t.drawImage(s.img, 0, 0, W, H);
  ctx.globalAlpha = Math.min(1, (micro ? 0.8 : 0.78 + 0.16 * b.tier) * s.alpha);
  ctx.drawImage(tc, bx, by);
  ctx.globalAlpha = 1;
}

/* dev-only topology debugger (§97): guides, slots, bboxes, nucleus rings */
function drawDebug(ctx: CanvasRenderingContext2D, scene: FxScene) {
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 0.9;
  ring(ctx, scene.cx, scene.cy, scene.Ri, 0.7, "#FF00AA", 1);
  ring(ctx, scene.cx, scene.cy, scene.rMid, 0.7, "#FFAA00", 1);
  ring(ctx, scene.cx, scene.cy, scene.Ro, 0.7, "#00FF88", 1);
  ctx.font = "10px monospace";
  scene.slots.forEach((s, i) => {
    ctx.strokeStyle = "#00E5FF";
    ctx.lineWidth = 1;
    guidePath(ctx, s, 0, 1, 30);
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,0,170,.8)";
    ctx.strokeRect(s.bbox[0], s.bbox[1], s.bbox[2], s.bbox[3]);
    dot(ctx, s.P[0][0], s.P[0][1], 3, "#FF00AA", 1);
    ring(ctx, s.cx, s.cy, s.r, 0.9, "#FFFFFF", 1);
    ctx.fillStyle = "#FFF";
    ctx.fillText(`s${i} ${s.slug} I=${s.I.toFixed(2)}`, s.cx + s.r + 4, s.cy);
  });
  ctx.globalAlpha = 1;
}
